import type { Worksheet } from "exceljs";
import { EXCEL_DEFAULT_COLUMN_WIDTH_CHARS, EXCEL_DEFAULT_FONT_SIZE_PT, EXCEL_DEFAULT_ROW_HEIGHT_PT } from "../utils/excelToFortuneSheet";

/** Excel quantises a row height to whole screen pixels (0.75pt at 96dpi). Writing heights onto the same
 *  grid makes the round trip through the pixel-based preview (`rowlen`) give the exact height back. */
const ROW_HEIGHT_QUANTUM_PT = 0.75;

/** The row height one line of `fontSizePt` needs, on Excel's own scale: its default row is 15pt for its
 *  default 11pt font, and that ratio is what it grows a row by. Rounded UP onto the pixel grid, which also
 *  keeps the height clear of what the PDF renderer draws a single line at — a row that renderer had to
 *  grow past its stated height is the divergence this pass exists to prevent. */
export const lineHeightForFontPt = (fontSizePt: number): number =>
	Math.ceil((fontSizePt * EXCEL_DEFAULT_ROW_HEIGHT_PT) / EXCEL_DEFAULT_FONT_SIZE_PT / ROW_HEIGHT_QUANTUM_PT) * ROW_HEIGHT_QUANTUM_PT;

/** Ceiling on the lines one row is sized for: a pasted paragraph in a Notes cell would otherwise claim
 *  most of a printed page for a single employee. */
export const WRAPPED_ROW_MAX_LINES = 6;

/** How many lines one cell's text needs once wrapped to `widthChars`. Explicit newlines are forced breaks;
 *  Excel's width unit is roughly one character of the default font, so the width IS the character budget —
 *  the same approximation the monthly printout makes in `services/downloads.py`. */
export const wrappedLineCount = (text: string, widthChars: number): number => {
	if (!text || widthChars <= 0) return 1;
	return text.split("\n").reduce((lines, segment) => lines + Math.max(1, Math.ceil(segment.length / widthChars)), 0);
};

/**
 * Wrap every cell of a worksheet, and grow each row to the height its worst-wrapping cell needs. Run once
 * per sheet AFTER everything is rendered, so no later write drops `wrapText` off an alignment it replaces.
 *
 * The explicit heights are the point: neither the preview (which draws exactly the `rowlen` it is given)
 * nor the PDF renderer auto-fits the way Excel does, so a stated height is the only value all three agree
 * on — and a one-line row carrying a BIGGER font is the case that must be pinned. Merged cells are wrapped
 * but never drive a LINE COUNT, since a title merged across the sheet would measure against its first
 * column alone; their font still counts, Excel growing a row to its tallest.
 */
export const applyWrapText = (worksheet: Worksheet): void => {
	// Resolved once, not per cell: a full week-schedule export is ~28k cells, and `getColumn` walks the
	// sheet's column list on every call.
	const widths = (worksheet.columns ?? []).map((column) => column?.width ?? EXCEL_DEFAULT_COLUMN_WIDTH_CHARS);
	const columnWidth = (columnNumber: number): number => widths[columnNumber - 1] ?? EXCEL_DEFAULT_COLUMN_WIDTH_CHARS;

	worksheet.eachRow({ includeEmpty: true }, (row) => {
		let maxLines = 1;
		let largestFontPt = EXCEL_DEFAULT_FONT_SIZE_PT;

		row.eachCell({ includeEmpty: true }, (cell, columnNumber) => {
			cell.alignment = { ...cell.alignment, wrapText: true };
			// A cell with no font of its own prints at the workbook default — Excel's stated size, not a guess.
			largestFontPt = Math.max(largestFontPt, cell.font?.size ?? EXCEL_DEFAULT_FONT_SIZE_PT);
			if (cell.isMerged) return;
			maxLines = Math.max(maxLines, wrappedLineCount(cell.text, columnWidth(columnNumber)));
		});

		if (maxLines === 1 && largestFontPt <= EXCEL_DEFAULT_FONT_SIZE_PT) return;
		// Never below Excel's default row: a row of 8pt text has always been given the full 15pt, and
		// shrinking it now would reflow every sheet that has one.
		const lineHeightPt = Math.max(EXCEL_DEFAULT_ROW_HEIGHT_PT, lineHeightForFontPt(largestFontPt));
		const neededHeight = Math.min(maxLines, WRAPPED_ROW_MAX_LINES) * lineHeightPt;
		// Never shrink a height a renderer set deliberately (a spacer row, or a week row already sized
		// to its stacked entries) — this pass only ever adds the space wrapping needs.
		row.height = Math.max(row.height ?? 0, neededHeight);
	});
};
